import { useState } from "react";
import { UserAvatar } from "@/components/UserAvatar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChevronDown, Star } from "lucide-react";
import type { PersonBoard } from "@/hooks/usePeopleBoard";
import { AFTER_VISIBLE } from "./people-constants";
import { PersonDeliverableRow } from "./PersonDeliverableRow";

const Kicker = ({ children }: { children: React.ReactNode }) => (
  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{children}</p>
);

export function PersonCard({ board }: { board: PersonBoard }) {
  const [afterOpen, setAfterOpen] = useState(false);
  const total = board.focus.length + board.queue.length + board.after.length;
  const afterShown = afterOpen ? board.after : board.after.slice(0, AFTER_VISIBLE);
  const afterHidden = board.after.length - afterShown.length;

  return (
    <div className="flex flex-col gap-3 rounded-xl border bg-card p-4">
      <div className="flex items-center gap-2.5">
        <UserAvatar src={board.user.avatarUrl} name={board.user.fullName} className="h-9 w-9" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{board.user.fullName}</div>
          <div className="text-xs text-muted-foreground">
            {total} open · {board.focus.length} focus · {board.queue.length} queue
          </div>
        </div>
      </div>

      <div>
        <Kicker>Focus</Kicker>
        {board.focus.length > 0 ? (
          <div className="space-y-1.5">{board.focus.map((e) => <PersonDeliverableRow key={e.priority.pid} entry={e} hero />)}</div>
        ) : (
          <div className="flex items-center gap-2 rounded-lg border border-dashed px-2.5 py-2 text-xs text-muted-foreground">
            <Star className="h-3.5 w-3.5" /> No focus set
          </div>
        )}
      </div>

      {board.queue.length > 0 && (
        <div>
          <Kicker>Queue</Kicker>
          <div className="space-y-0.5">
            {board.queue.map((e, i) => <PersonDeliverableRow key={e.priority.pid} entry={e} index={i + 1} />)}
          </div>
        </div>
      )}

      {board.after.length > 0 && (
        <Collapsible open={afterOpen} onOpenChange={setAfterOpen}>
          <Kicker>After that</Kicker>
          <div className="space-y-0.5">
            {afterShown.map((e) => <PersonDeliverableRow key={e.priority.pid} entry={e} />)}
          </div>
          {board.after.length > AFTER_VISIBLE && (
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="mt-1 h-7 gap-1 px-2 text-xs text-muted-foreground">
                {afterOpen ? "Show less" : `+${afterHidden} more`}
                <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", afterOpen && "rotate-180")} />
              </Button>
            </CollapsibleTrigger>
          )}
          <CollapsibleContent />
        </Collapsible>
      )}
    </div>
  );
}
